/**
 * FullVSL YouTube IFrame API player (FUL-243).
 *
 * A full-featured player for YouTube source URLs that parallels the HTML5
 * `FullVslPlayer` in public API and event vocabulary, but uses the YouTube
 * IFrame API for media playback.
 *
 * Reuses the same collaborator controllers (CtaController, OverlayController,
 * ResumeWiring, EmbedBridge) and the same CSS/icons/utilities from the HTML5
 * player package. The YouTubeBackend wraps the YT IFrame API and exposes a
 * PlayerMediaLike interface so the controllers work without modification.
 *
 * Key behaviors:
 *  - YouTube native controls hidden (playerVars.controls=0); FullVSL renders
 *    its own custom controls bar.
 *  - Overlays/CTA/resume rendered OUTSIDE the YouTube iframe (FullVSL DOM
 *    layers above the iframe region), so they work identically to HTML5 mode.
 *  - Progress tracked via polling (YouTube has no timeupdate event).
 *  - Resume seeks via `backend.currentTime = sec` → `YT.Player.seekTo()`.
 *  - Does NOT promise or attempt YouTube branding removal (PRD §7.2).
 */

import type {
  AutoplayConfig,
  CtaConfig,
  DelayedElement,
  Overlay,
  PlayerEvent,
  PlayerEventDispatcher,
  ProgressBarConfig,
  ResumeConfig,
} from "../../contracts/src/contracts";
import {
  CtaController,
  advanceMaxWatched,
  type WatchProgress,
} from "./fullvsl-cta";
import {
  OverlayController,
  type OverlayActionHandlers,
} from "./fullvsl-overlay";
import {
  OVERLAY_POSITIONS,
  resolveSafeZones,
  resolveSafeZonesByOrientation,
  type SafeZoneLayout,
  type SafeZoneRegion,
} from "./fullvsl-safe-zones";
import { EmbedBridge } from "./embed-bridge";
import { DEFAULT_RESUME_CONFIG, ResumeWiring } from "./resume-wiring";
import {
  fromVisualProgress,
  normalizeProgressBarConfig,
  toVisualProgress,
} from "./progress-curve";
import {
  classifyAspectRatio,
  fmtTime,
  generateId,
  getOrCreateVisitorId,
  injectPlayerCss,
  ICON_PLAY,
  ICON_PAUSE,
  ICON_MUTED,
  ICON_VOL,
  ICON_FS,
  resolveOrientation,
  type AspectRatioInput,
  type DetectedAspect,
  type Orientation,
  type StandardAspectRatio,
} from "./fullvsl-player";
import {
  YouTubeBackend,
  parseYouTubeVideoId,
  type YouTubeBackendCallbacks,
} from "./youtube-backend";

const MILESTONES = [25, 50, 75, 100] as const;
const PROGRESS_MIN_INTERVAL_MS = 1000;
const PROGRESS_MIN_PCT_STEP = 5;

/** Extra CSS for the YouTube-specific host element (iframe sizing). */
const YT_CSS = `
.fvsl-yt-host{position:absolute;inset:0;width:100%;height:100%;z-index:0;background:#000}
.fvsl-yt-host iframe{width:100%!important;height:100%!important;border:0;position:absolute;inset:0}
.fvsl-yt-error{position:absolute;inset:0;z-index:8;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.85);color:#fff;font:14px/1.5 system-ui,-apple-system,sans-serif;text-align:center;padding:20px;pointer-events:auto}
`;

let ytCssInjected = false;
function injectYtCss(): void {
  if (ytCssInjected) return;
  ytCssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-fullvsl-youtube", "");
  style.textContent = YT_CSS;
  document.head.appendChild(style);
}

export interface YouTubePlayerOptions {
  /** YouTube URL (watch, youtu.be, embed, shorts, live, or bare video ID). */
  sourceUrl: string;
  /** Host element the player mounts into. */
  container: HTMLElement;
  aspectRatio?: AspectRatioInput;
  customRatio?: { w: number; h: number };
  muted?: boolean;
  loop?: boolean;
  showSafeZones?: boolean;
  onAspectDetected?: (aspect: DetectedAspect) => void;
  onOrientationChange?: (orientation: Orientation) => void;
  cta?: CtaConfig;
  delayedElements?: DelayedElement[];
  overlays?: Overlay[];
  ctaHeadline?: string;
  ctaSubtext?: string;
  ctaButtonLabel?: string;
  onEvent?: PlayerEventDispatcher;
  vslId?: string;
  sessionId?: string;
  visitorId?: string;
  autoplay?: AutoplayConfig;
  progressBar?: ProgressBarConfig;
  brandColor?: string;
  emitAnalyticsToParent?: boolean;
  resume?: ResumeConfig;
  funnelId?: string;
  pageId?: string;
  allowedParentOrigins?: string[];
}

export class YouTubePlayer {
  private readonly opts: YouTubePlayerOptions;
  private readonly frame: HTMLElement;
  private readonly ytHost: HTMLElement;
  private readonly played: HTMLElement;
  private readonly buffered: HTMLElement;
  private readonly timeLabel: HTMLElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly muteBtn: HTMLButtonElement;
  private readonly fsBtn: HTMLButtonElement;
  private readonly bigPlay: HTMLButtonElement;
  private readonly seek: HTMLElement;
  private readonly safeZoneLayer: HTMLElement;
  private readonly ctaLayer: HTMLElement;
  private readonly overlayLayer: HTMLElement;
  private readonly resumeLayer: HTMLElement;
  private readonly errorLayer: HTMLElement;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private lastAppliedOrientation: Orientation = "landscape";
  private detectedAspectField: DetectedAspect | null = null;
  private ctaController: CtaController | null = null;
  private overlayController: OverlayController | null = null;
  private readonly embedBridge: EmbedBridge;
  private resumeWiring: ResumeWiring | null = null;
  private autoplayEngaged = false;
  private fullscreenBlocked = false;
  private maxWatchedSec = 0;
  private lastCurrentTimeSec = 0;
  private eventSeq = 0;
  private lastProgressEmitMs = 0;
  private lastProgressEmitPct = -1;
  private readonly milestonesHit = new Set<number>();
  private readonly sessionId: string;
  private readonly visitorId: string;
  private readonly vslId: string;
  private readonly backend: YouTubeBackend;
  private destroyed = false;

  constructor(opts: YouTubePlayerOptions) {
    this.opts = { ...opts, progressBar: normalizeProgressBarConfig(opts.progressBar) };
    injectPlayerCss();
    injectYtCss();

    this.vslId = opts.vslId || "demo-vsl";
    this.sessionId = opts.sessionId || generateId("sess");
    this.visitorId = opts.visitorId || getOrCreateVisitorId();

    this.embedBridge = new EmbedBridge({
      vslId: this.vslId,
      allowedParentOrigins: opts.allowedParentOrigins,
    });

    this.frame = document.createElement("div");
    this.frame.className = "fvsl-frame fvsl-paused";
    this.frame.style.setProperty("--fvsl-brand", opts.brandColor || "#00685f");
    this.frame.dataset.orientation = "landscape";

    // YouTube iframe host element.
    this.ytHost = document.createElement("div");
    this.ytHost.className = "fvsl-yt-host";

    this.bigPlay = document.createElement("button");
    this.bigPlay.type = "button";
    this.bigPlay.className = "fvsl-bigplay";
    this.bigPlay.setAttribute("aria-label", "Reproduzir");
    this.bigPlay.innerHTML = ICON_PLAY;

    const center = document.createElement("div");
    center.className = "fvsl-center";
    center.appendChild(this.bigPlay);

    this.playBtn = this.mkBtn("play", ICON_PLAY);
    this.muteBtn = this.mkBtn("mute", opts.muted ? ICON_MUTED : ICON_VOL);
    this.fsBtn = this.mkBtn("fullscreen", ICON_FS);

    this.played = document.createElement("div");
    this.played.className = "fvsl-seek-played";
    this.played.style.width = "0%";
    this.buffered = document.createElement("div");
    this.buffered.className = "fvsl-seek-buffered";
    this.buffered.style.width = "0%";
    const track = document.createElement("div");
    track.className = "fvsl-seek-track";
    track.appendChild(this.buffered);
    track.appendChild(this.played);
    this.seek = document.createElement("div");
    this.seek.className = "fvsl-seek";
    this.seek.setAttribute("role", "slider");
    this.seek.setAttribute("aria-label", "Avançar ou voltar");
    this.seek.appendChild(track);

    this.timeLabel = document.createElement("span");
    this.timeLabel.className = "fvsl-time";
    this.timeLabel.textContent = "0:00 / 0:00";

    const ctrl = document.createElement("div");
    ctrl.className = "fvsl-ctrl is-visible";
    ctrl.appendChild(this.playBtn);
    ctrl.appendChild(this.muteBtn);
    ctrl.appendChild(this.seek);
    ctrl.appendChild(this.timeLabel);
    ctrl.appendChild(this.fsBtn);

    this.safeZoneLayer = document.createElement("div");
    this.safeZoneLayer.className = "fvsl-safezones";
    this.safeZoneLayer.setAttribute("aria-hidden", "true");

    this.ctaLayer = document.createElement("div");
    this.ctaLayer.className = "fvsl-cta-layer";

    this.overlayLayer = document.createElement("div");
    this.overlayLayer.className = "fvsl-overlay-layer";

    this.resumeLayer = document.createElement("div");
    this.resumeLayer.className = "fvsl-resume-layer";

    this.errorLayer = document.createElement("div");
    this.errorLayer.className = "fvsl-yt-error";
    this.errorLayer.style.display = "none";

    this.frame.appendChild(this.ytHost);
    this.frame.appendChild(center);
    this.frame.appendChild(this.safeZoneLayer);
    this.frame.appendChild(this.ctaLayer);
    this.frame.appendChild(this.overlayLayer);
    this.frame.appendChild(this.resumeLayer);
    this.frame.appendChild(this.errorLayer);
    this.frame.appendChild(ctrl);
    opts.container.appendChild(this.frame);

    // Resume wiring.
    this.resumeWiring = new ResumeWiring({
      vslId: this.vslId,
      visitorId: this.visitorId,
      sourceUrl: opts.sourceUrl,
      config: opts.resume ?? DEFAULT_RESUME_CONFIG,
      funnelId: opts.funnelId,
      pageId: opts.pageId,
      emit: (e) => this.dispatchEvent(e),
    });

    // CTA controller.
    if (opts.cta) {
      this.ctaController = new CtaController({
        media: this.backendAdapter(),
        layer: this.ctaLayer,
        config: opts.cta,
        delayedElements: opts.delayedElements,
        headline: opts.ctaHeadline,
        subtext: opts.ctaSubtext,
        buttonLabel: opts.ctaButtonLabel,
        emit: (ctaEvent) => this.dispatchEvent(ctaEvent),
        onNavigate: (url) => this.openCtaDestination(url),
        onNavigateBlocked: (url) =>
          this.embedBridge.sendNavigate(url, opts.cta?.destination.type ?? "url"),
        onDelayedReveal: (selector) => this.embedBridge.sendReveal(selector),
        onCustomEvent: (name, detail) => this.embedBridge.sendCustomEvent(name, detail),
      });
    }

    // Overlay controller.
    if (opts.overlays && opts.overlays.length > 0) {
      this.overlayController = new OverlayController({
        layer: this.overlayLayer,
        overlays: opts.overlays,
        ctaReveal: opts.cta
          ? {
              revealMode: opts.cta.revealMode,
              revealAt: opts.cta.revealAt,
              persistAfterReveal: opts.cta.persistAfterReveal,
            }
          : undefined,
        getLayout: () => this.resolveOverlayLayout(),
        emit: (event) => this.dispatchEvent(event),
        actions: this.buildOverlayActionHandlers(),
      });
    }

    this.applyAspectRatio(opts.aspectRatio ?? "auto");
    this.bindControls();

    // Create YouTube backend.
    const videoId = parseYouTubeVideoId(opts.sourceUrl);
    if (!videoId) {
      this.showError(
        "Could not extract a YouTube video ID from the provided URL. " +
          "Please check the URL and try again.",
      );
    }

    const ytCallbacks: YouTubeBackendCallbacks = {
      onReady: () => this.onBackendReady(),
      onPlay: () => this.onPlay(),
      onPause: () => this.onPause(),
      onEnded: () => this.onEnded(),
      onTimeUpdate: (sec) => this.onTimeUpdate(sec),
      onBufferedFraction: (frac) => this.onBufferedFraction(frac),
      onError: (_code, msg) => this.showError(msg),
    };

    this.backend = new YouTubeBackend({
      hostEl: this.ytHost,
      videoId: videoId ?? "",
      muted: opts.muted,
      callbacks: ytCallbacks,
    });
  }

  /**
   * Minimal adapter object that satisfies PlayerMediaLike by delegating
   * currentTime/duration reads and seeks to the YouTube backend.
   */
  private backendAdapter(): { currentTime: number; readonly duration: number } {
    const getBackend = (): YouTubeBackend => this.backend;
    return {
      get currentTime(): number {
        return getBackend().currentTime;
      },
      set currentTime(v: number) {
        getBackend().currentTime = v;
      },
      get duration(): number {
        return getBackend().duration;
      },
    };
  }

  private resolveOverlayLayout(): SafeZoneLayout {
    return this.detectedAspectField
      ? resolveSafeZones(this.detectedAspectField)
      : resolveSafeZonesByOrientation(this.lastAppliedOrientation);
  }

  private buildOverlayActionHandlers(): OverlayActionHandlers {
    return {
      continue: () => {
        if (this.backend.muted) this.backend.unMute();
        this.backend.playVideo();
      },
      restart: () => {
        this.backend.currentTime = 0;
        this.backend.playVideo();
      },
      jumpToOffer: () => this.openCtaDestination(),
      openCta: () => {
        this.ctaController?.show({
          currentTimeSec: this.backend.currentTime,
          maxWatchedSec: this.maxWatchedSec,
          durationSec: Number.isFinite(this.backend.duration) ? this.backend.duration : 0,
        });
      },
    };
  }

  private openCtaDestination(explicitUrl?: string): boolean {
    const dest = this.opts.cta?.destination;
    if (!dest) return false;
    const isNavigate = ["url", "funnel_checkout", "form", "calendar"].includes(dest.type);
    if (!isNavigate) return false;
    const url = explicitUrl ?? dest.value;
    if (!url) return false;
    try {
      const handle = window.open(url, "_blank", "noopener,noreferrer");
      if (handle) return true;
    } catch {
      /* popup blocked */
    }
    this.embedBridge.sendNavigate(url, dest.type);
    return false;
  }

  private mkBtn(name: string, icon: string): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `fvsl-btn fvsl-btn-${name}`;
    b.setAttribute("aria-label", name);
    b.innerHTML = icon;
    return b;
  }

  private bindControls(): void {
    this.playBtn.addEventListener("click", this.togglePlay);
    this.bigPlay.addEventListener("click", this.togglePlay);
    this.muteBtn.addEventListener("click", this.toggleMute);
    this.fsBtn.addEventListener("click", this.toggleFullscreen);

    this.seek.addEventListener("pointerdown", this.onSeekStart);
    this.frame.addEventListener("pointermove", this.scheduleHide);
    this.frame.addEventListener("pointerleave", this.hideControlsSoon);

    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  }

  private applyAspectRatio(input: AspectRatioInput): void {
    let cssValue = "16 / 9";
    let orientation: Orientation = "landscape";
    let label: StandardAspectRatio | "custom" | "auto" = input;

    if (input === "auto") {
      // YouTube videos are almost always 16:9.
      cssValue = "16 / 9";
      orientation = "landscape";
      label = "16:9";
      this.detectedAspectField = classifyAspectRatio(16, 9);
      this.opts.onAspectDetected?.(this.detectedAspectField);
    } else if (input === "custom") {
      const cw = this.opts.customRatio?.w ?? 16;
      const ch = this.opts.customRatio?.h ?? 9;
      cssValue = `${cw} / ${ch}`;
      orientation = resolveOrientation(cw, ch);
      this.detectedAspectField = classifyAspectRatio(cw, ch);
    } else {
      const [w, h] = input.split(":").map(Number);
      cssValue = `${w} / ${h}`;
      orientation = resolveOrientation(w, h);
      this.detectedAspectField = classifyAspectRatio(w, h);
    }

    this.frame.style.aspectRatio = cssValue;
    this.frame.dataset.orientation = orientation;
    this.frame.dataset.aspectLabel = label;
    this.lastAppliedOrientation = orientation;
    this.opts.onOrientationChange?.(orientation);
    this.renderSafeZones();
  }

  private renderSafeZones(): void {
    const layer = this.safeZoneLayer;
    layer.innerHTML = "";
    if (!this.opts.showSafeZones) {
      layer.classList.remove("is-on");
      this.overlayController?.relayout();
      return;
    }
    layer.classList.add("is-on");
    const layout = this.detectedAspectField
      ? resolveSafeZones(this.detectedAspectField)
      : resolveSafeZonesByOrientation(this.lastAppliedOrientation);
    layer.dataset.label = layout.label;
    for (const position of OVERLAY_POSITIONS) {
      const region: SafeZoneRegion = layout.regions[position];
      const cell = document.createElement("div");
      cell.className = "fvsl-safezone";
      cell.dataset.position = position;
      cell.style.left = `${(region.x * 100).toFixed(3)}%`;
      cell.style.top = `${(region.y * 100).toFixed(3)}%`;
      cell.style.width = `${(region.width * 100).toFixed(3)}%`;
      cell.style.height = `${(region.height * 100).toFixed(3)}%`;
      const tag = document.createElement("span");
      tag.className = "fvsl-safezone-tag";
      tag.textContent = position;
      cell.appendChild(tag);
      layer.appendChild(cell);
    }
    this.overlayController?.relayout();
  }

  /* ---- YouTube backend event handlers ---- */

  private onBackendReady(): void {
    if (this.destroyed) return;
    // Restore resume state now that duration is known.
    this.restoreResume();
    // Attempt autoplay if configured.
    this.attemptAutoplay();
  }

  private onPlay(): void {
    if (this.destroyed) return;
    this.frame.classList.add("fvsl-playing");
    this.frame.classList.remove("fvsl-paused");
    this.playBtn.innerHTML = ICON_PAUSE;
    this.playBtn.setAttribute("aria-label", "Pausar");
    this.hideControlsSoon();
    this.dispatchEvent(this.buildSimpleEvent("play"));
    this.overlayController?.notify({ hasStarted: true, isPaused: false });
  }

  private onPause(): void {
    if (this.destroyed) return;
    this.frame.classList.remove("fvsl-playing");
    this.frame.classList.add("fvsl-paused");
    this.playBtn.innerHTML = ICON_PLAY;
    this.playBtn.setAttribute("aria-label", "Reproduzir");
    this.showControls();
    if (this.backend.isEnded) return;
    this.dispatchEvent(this.buildSimpleEvent("pause"));
    this.ctaController?.notifyPause();
    this.overlayController?.notify({ isPaused: true });
    this.checkpointResume();
  }

  private onEnded(): void {
    if (this.destroyed) return;
    const dur = Number.isFinite(this.backend.duration) ? this.backend.duration : 0;
    if (dur > 0) this.maxWatchedSec = Math.max(this.maxWatchedSec, dur);
    this.dispatchEvent(this.buildSimpleEvent("completed"));
    this.ctaController?.notifyComplete();
    this.ctaController?.update({
      currentTimeSec: this.backend.currentTime,
      maxWatchedSec: this.maxWatchedSec,
      durationSec: dur,
    });
    this.overlayController?.notify({ completed: true, isPaused: false });
    this.resumeWiring?.clear();
  }

  private onTimeUpdate(sec: number): void {
    if (this.destroyed) return;
    const dur = Number.isFinite(this.backend.duration) ? this.backend.duration : 0;
    const realRatio = dur > 0 ? sec / dur : 0;
    this.played.style.width = `${toVisualProgress(realRatio, this.opts.progressBar) * 100}%`;
    this.timeLabel.textContent = `${fmtTime(sec)} / ${fmtTime(dur)}`;

    if (!this.backend.seeking) {
      this.maxWatchedSec = advanceMaxWatched(this.maxWatchedSec, sec, this.lastCurrentTimeSec);
    }
    this.lastCurrentTimeSec = sec;

    const progress: WatchProgress = {
      currentTimeSec: sec,
      maxWatchedSec: this.maxWatchedSec,
      durationSec: dur,
    };

    this.ctaController?.update(progress);
    this.overlayController?.update(progress);
    this.emitProgressMaybe(progress);
    this.emitMilestonesMaybe(progress);
    this.checkpointResume();
  }

  private onBufferedFraction(frac: number): void {
    this.buffered.style.width = `${toVisualProgress(frac, this.opts.progressBar) * 100}%`;
  }

  /* ---- Controls ---- */

  togglePlay = (): void => {
    if (this.backend.isPlaying) {
      this.backend.pauseVideo();
    } else {
      this.backend.playVideo();
    }
  };

  toggleMute = (): void => {
    if (this.backend.muted) {
      this.backend.unMute();
    } else {
      this.backend.mute();
    }
    this.muteBtn.innerHTML = this.backend.muted ? ICON_MUTED : ICON_VOL;
    this.muteBtn.setAttribute("aria-label", this.backend.muted ? "Ativar som" : "Silenciar");
    this.overlayController?.notify({ isMuted: this.backend.muted });
  };

  toggleFullscreen = (): void => {
    const isFs = document.fullscreenElement === this.frame;
    if (!isFs) {
      const canFs = typeof this.frame.requestFullscreen === "function";
      if (!canFs) {
        this.onFullscreenUnavailable();
        return;
      }
      this.frame
        .requestFullscreen()
        .then(() => {
          this.fullscreenBlocked = false;
        })
        .catch(() => {
          this.onFullscreenUnavailable();
        });
    } else {
      void document.exitFullscreen?.().catch(() => {});
      this.embedBridge.sendFullscreen("exit");
    }
  };

  private onFullscreenUnavailable(): void {
    if (this.fullscreenBlocked) return;
    this.fullscreenBlocked = true;
    this.embedBridge.sendFullscreen("enter");
    this.showToast("A tela cheia não está disponível nesta incorporação");
  }

  private showToast(text: string): void {
    const toast = document.createElement("div");
    toast.className = "fvsl-toast";
    toast.setAttribute("role", "status");
    toast.textContent = text;
    this.frame.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add("is-visible"));
    setTimeout(() => {
      toast.classList.remove("is-visible");
      setTimeout(() => toast.remove(), 250);
    }, 2600);
  }

  private onFullscreenChange = (): void => {
    const isFs = document.fullscreenElement === this.frame;
    this.frame.classList.toggle("fvsl-fullscreen", isFs);
    if (isFs) this.frame.style.maxWidth = "none";
    else this.frame.style.maxWidth = "";
  };

  private seekToClientX = (clientX: number): void => {
    const rect = this.seek.getBoundingClientRect();
    if (rect.width <= 0) return;
    const visualRatio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const realRatio = fromVisualProgress(visualRatio, this.opts.progressBar);
    const dur = this.backend.duration;
    if (Number.isFinite(dur) && dur > 0) {
      this.backend.currentTime = realRatio * dur;
      this.played.style.width = `${visualRatio * 100}%`;
    }
  };

  private onSeekStart = (ev: PointerEvent): void => {
    ev.preventDefault();
    this.seek.setPointerCapture(ev.pointerId);
    this.seekToClientX(ev.clientX);
    const move = (e: PointerEvent) => this.seekToClientX(e.clientX);
    const up = (e: PointerEvent) => {
      this.seekToClientX(e.clientX);
      this.seek.releasePointerCapture(ev.pointerId);
      this.seek.removeEventListener("pointermove", move);
      this.seek.removeEventListener("pointerup", up);
      this.seek.removeEventListener("pointercancel", up);
    };
    this.seek.addEventListener("pointermove", move);
    this.seek.addEventListener("pointerup", up);
    this.seek.addEventListener("pointercancel", up);
  };

  private showControls(): void {
    const ctrl = this.frame.querySelector(".fvsl-ctrl") as HTMLElement | null;
    ctrl?.classList.add("is-visible");
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  private hideControlsSoon = (): void => {
    this.showControls();
    if (this.backend.isPaused) return;
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      const ctrl = this.frame.querySelector(".fvsl-ctrl") as HTMLElement | null;
      ctrl?.classList.remove("is-visible");
    }, 2400);
  };

  private scheduleHide = (): void => {
    this.hideControlsSoon();
  };

  private attemptAutoplay(): void {
    const cfg = this.opts.autoplay;
    if (!cfg || cfg.enabled === false) return;
    const wantMuted = cfg.muted;
    if (wantMuted) {
      this.backend.mute();
    } else {
      this.backend.unMute();
    }
    this.autoplayEngaged = true;
    this.dispatchEvent(this.buildSimpleEvent("autoplay_impression"));
    this.overlayController?.notify({ isMuted: wantMuted, hasStarted: true });
    this.backend.playVideo();
  }

  /* ---- Resume ---- */

  private restoreResume(): void {
    if (!this.resumeWiring) return;
    const dur = Number.isFinite(this.backend.duration) ? this.backend.duration : 0;
    const result = this.resumeWiring.restore(this.backendAdapter(), dur);
    if (result.shouldPrompt) this.showResumePrompt(result.promptFromSec);
  }

  private checkpointResume(): void {
    if (!this.resumeWiring) return;
    if (this.resumePromptVisible) return;
    const dur = Number.isFinite(this.backend.duration) ? this.backend.duration : 0;
    this.resumeWiring.checkpoint({
      positionSec: this.backend.currentTime,
      maxWatchedSec: this.maxWatchedSec,
      durationSec: dur,
      ctaShown: this.ctaController?.isVisible,
    });
  }

  private showResumePrompt(fromSec: number): void {
    if (this.resumeLayer.querySelector(".fvsl-resume")) return;
    const card = document.createElement("div");
    card.className = "fvsl-resume";
    card.setAttribute("role", "dialog");
    card.setAttribute("aria-label", "Retomar reprodução");

    const text = document.createElement("span");
    text.className = "fvsl-resume-text";
    text.textContent = `Continuar de ${fmtTime(fromSec)}?`;

    const resumeBtn = document.createElement("button");
    resumeBtn.type = "button";
    resumeBtn.className = "fvsl-resume-btn";
    resumeBtn.textContent = "Continuar";

    const restartBtn = document.createElement("button");
    restartBtn.type = "button";
    restartBtn.className = "fvsl-resume-btn fvsl-resume-btn--ghost";
    restartBtn.textContent = "Reiniciar";

    resumeBtn.addEventListener("click", () => {
      this.resumeWiring?.acceptResume(this.backendAdapter());
      card.remove();
    });
    restartBtn.addEventListener("click", () => {
      this.resumeWiring?.declineResume();
      this.resumeWiring?.clear();
      card.remove();
    });

    card.appendChild(text);
    card.appendChild(resumeBtn);
    card.appendChild(restartBtn);
    this.resumeLayer.appendChild(card);
    requestAnimationFrame(() => card.classList.add("is-visible"));
  }

  /* ---- Error ---- */

  private showError(message: string): void {
    if (this.destroyed) return;
    this.errorLayer.style.display = "flex";
    this.errorLayer.textContent = message;
    this.dispatchEvent({
      seq: ++this.eventSeq,
      name: "error",
      clientTs: new Date().toISOString(),
      vslId: this.vslId,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      code: "youtube_error",
      message,
      sourceType: "youtube",
    });
  }

  /* ---- Event dispatching (PlayerEvent vocabulary) ---- */

  private progressClock(): { cur: number; dur: number } {
    const cur = this.backend.currentTime;
    const dur = this.backend.duration;
    return { cur, dur: Number.isFinite(dur) ? dur : 0 };
  }

  private buildSimpleEvent(
    name: "impression" | "autoplay_impression" | "play" | "pause" | "completed",
  ): PlayerEvent {
    const { cur, dur } = this.progressClock();
    return {
      seq: ++this.eventSeq,
      name,
      clientTs: new Date().toISOString(),
      vslId: this.vslId,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      positionSec: cur,
      durationSec: dur,
    };
  }

  private dispatchEvent(event: PlayerEvent): void {
    const enriched: PlayerEvent = {
      ...event,
      seq: ++this.eventSeq,
      clientTs: new Date().toISOString(),
      vslId: this.vslId,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
    };
    this.opts.onEvent?.(enriched);
    if (this.opts.emitAnalyticsToParent !== false) this.embedBridge.sendEvent(enriched);
    if (enriched.name === "cta_shown") {
      this.resumeWiring?.markCtaShown(enriched.positionSec ?? 0, this.maxWatchedSec);
    }
  }

  private emitProgressMaybe(progress: WatchProgress): void {
    if (progress.durationSec <= 0) return;
    const now = Date.now();
    const pct = (progress.maxWatchedSec / progress.durationSec) * 100;
    const sinceMs = now - this.lastProgressEmitMs;
    const pctStep = Math.abs(pct - this.lastProgressEmitPct);
    const due = sinceMs >= PROGRESS_MIN_INTERVAL_MS && pctStep >= PROGRESS_MIN_PCT_STEP;
    if (!due) return;
    this.lastProgressEmitMs = now;
    this.lastProgressEmitPct = pct;
    this.dispatchEvent({
      seq: 0,
      name: "progress",
      clientTs: "",
      vslId: "",
      sessionId: "",
      visitorId: "",
      positionSec: progress.currentTimeSec,
      maxWatchedSec: progress.maxWatchedSec,
      durationSec: progress.durationSec,
      pct: Math.round(pct * 100) / 100,
    });
  }

  private emitMilestonesMaybe(progress: WatchProgress): void {
    if (progress.durationSec <= 0) return;
    const watchedPct = (progress.maxWatchedSec / progress.durationSec) * 100;
    for (const m of MILESTONES) {
      if (watchedPct >= m && !this.milestonesHit.has(m)) {
        this.milestonesHit.add(m);
        this.dispatchEvent({
          seq: 0,
          name: "milestone",
          clientTs: "",
          vslId: "",
          sessionId: "",
          visitorId: "",
          milestone: m as 25 | 50 | 75 | 100,
          positionSec: progress.maxWatchedSec,
          durationSec: progress.durationSec,
        });
      }
    }
  }

  /* ---- Public API (mirrors FullVslPlayer) ---- */

  play(): Promise<void> {
    this.backend.playVideo();
    return Promise.resolve();
  }

  pause(): void {
    this.backend.pauseVideo();
  }

  openOffer(): boolean {
    return this.openCtaDestination();
  }

  revealHostElement(selector: string): void {
    this.embedBridge.sendReveal(selector);
  }

  acceptResume(): void {
    this.resumeWiring?.acceptResume(this.backendAdapter());
    const card = this.resumeLayer.querySelector(".fvsl-resume");
    card?.remove();
  }

  declineResume(): void {
    this.resumeWiring?.declineResume();
    this.resumeWiring?.clear();
    const card = this.resumeLayer.querySelector(".fvsl-resume");
    card?.remove();
  }

  get autoplayStarted(): boolean {
    return this.autoplayEngaged;
  }

  get resumePromptVisible(): boolean {
    return !!this.resumeLayer.querySelector(".fvsl-resume");
  }

  setAspectRatio(input: AspectRatioInput, customRatio?: { w: number; h: number }): void {
    if (customRatio) this.opts.customRatio = customRatio;
    this.opts.aspectRatio = input;
    this.applyAspectRatio(input);
  }

  setShowSafeZones(show: boolean): void {
    this.opts.showSafeZones = show;
    this.renderSafeZones();
  }

  get detectedAspect(): DetectedAspect | null {
    return this.detectedAspectField;
  }

  get maxWatchedSeconds(): number {
    return this.maxWatchedSec;
  }

  get ctaVisible(): boolean {
    return this.ctaController?.isVisible ?? false;
  }

  setOverlays(overlays: Overlay[]): void {
    this.opts.overlays = overlays;
    if (overlays.length === 0) {
      this.overlayController?.destroy();
      this.overlayController = null;
      return;
    }
    if (this.overlayController) {
      this.overlayController.setOverlays(overlays);
    } else {
      this.overlayController = new OverlayController({
        layer: this.overlayLayer,
        overlays,
        ctaReveal: this.opts.cta
          ? {
              revealMode: this.opts.cta.revealMode,
              revealAt: this.opts.cta.revealAt,
              persistAfterReveal: this.opts.cta.persistAfterReveal,
            }
          : undefined,
        getLayout: () => this.resolveOverlayLayout(),
        emit: (event) => this.dispatchEvent(event),
        actions: this.buildOverlayActionHandlers(),
      });
    }
  }

  isOverlayVisible(overlayId: string): boolean {
    return this.overlayController?.isVisible(overlayId) ?? false;
  }

  destroy(): void {
    this.destroyed = true;
    this.checkpointResume();
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.ctaController?.destroy();
    this.ctaController = null;
    this.overlayController?.destroy();
    this.overlayController = null;
    this.backend.destroy();
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    this.frame.remove();
  }
}
